import { useRef, useState } from "react";
import { POSE_VALIDATION, TIMING } from "../constants/interviewInstructions";

const { LANDMARK_VISIBILITY_THRESHOLD: VISIBLE } = POSE_VALIDATION;

const OUTLINE_COLOR = "#ffffff";
const OUTLINE_THICKNESS = 1.2;

export const drawDarkOutline = (ctx, image, x, y, w, h) => {
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.filter = "none";
  const t = OUTLINE_THICKNESS;
  const offsets = [
    [0, 0],
    [-t, 0],
    [t, 0],
    [0, -t],
    [0, t],
    [-t, -t],
    [t, -t],
    [-t, t],
    [t, t],
  ];
  offsets.forEach(([dx, dy]) => {
    ctx.drawImage(image, x + dx, y + dy, w, h);
  });
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = OUTLINE_COLOR;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.restore();
};

const validateBodyVisible = (pose) => {
  if (!pose) return false;

  const leftShoulder = pose[11];
  const rightShoulder = pose[12];
  const nose = pose[0];

  // Check if essential upper body landmarks are visible
  const noseVisible = nose && nose.visibility > VISIBLE;
  const leftShoulderVisible = leftShoulder && leftShoulder.visibility > VISIBLE;
  const rightShoulderVisible = rightShoulder && rightShoulder.visibility > VISIBLE;

  if (!noseVisible || !leftShoulderVisible || !rightShoulderVisible) {
    return false;
  }

  // Check vertical positioning - head should be above shoulders
  const headAboveShoulders =
    nose.y < Math.min(leftShoulder.y, rightShoulder.y);

  const avgShoulderY = (leftShoulder.y + rightShoulder.y) / 2;

  const expectedUpperChestY = avgShoulderY + 0.02;
  const upperChestAreaVisible = expectedUpperChestY < POSE_VALIDATION.UPPER_CHEST_MAX_Y;

  const adequateUpperBodyCoverage =
    avgShoulderY - nose.y > POSE_VALIDATION.HEAD_SHOULDER_GAP_MIN &&
    avgShoulderY - nose.y < POSE_VALIDATION.HEAD_SHOULDER_GAP_MAX;

  return headAboveShoulders && upperChestAreaVisible && adequateUpperBodyCoverage;
};

const validateUprightPosture = (pose) => {
  if (!pose) return false;

  const leftShoulder = pose[11];
  const rightShoulder = pose[12];
  const nose = pose[0];

  // Check if essential landmarks are visible
  const noseVisible = nose && nose.visibility > VISIBLE;
  const leftShoulderVisible = leftShoulder && leftShoulder.visibility > VISIBLE;
  const rightShoulderVisible = rightShoulder && rightShoulder.visibility > VISIBLE;

  if (!noseVisible || !leftShoulderVisible || !rightShoulderVisible) {
    return false;
  }

  // Check shoulder alignment (shoulders should be level)
  const shoulderLevelDiff = Math.abs(leftShoulder.y - rightShoulder.y);
  const shouldersLevel = shoulderLevelDiff < POSE_VALIDATION.SHOULDER_LEVEL_MAX_DIFF;

  // Check body alignment (nose should be roughly centered between shoulders)
  const shoulderCenter = (leftShoulder.x + rightShoulder.x) / 2;
  const bodyAlignmentDiff = Math.abs(nose.x - shoulderCenter);
  const bodyAligned = bodyAlignmentDiff < POSE_VALIDATION.BODY_ALIGNMENT_MAX_DIFF; // Person facing forward

  // Additional check: shoulders should be reasonably horizontal
  const shoulderWidth = Math.abs(leftShoulder.x - rightShoulder.x);
  const shouldersProperlySpaced =
    shoulderWidth > POSE_VALIDATION.SHOULDER_WIDTH_MIN &&
    shoulderWidth < POSE_VALIDATION.SHOULDER_WIDTH_MAX;

  return shouldersLevel && bodyAligned && shouldersProperlySpaced;
};

const validateCenteredPosition = (pose) => {
  if (!pose) return false;

  const nose = pose[0];
  const leftShoulder = pose[11];
  const rightShoulder = pose[12];

  if (!nose || !leftShoulder || !rightShoulder) return false;

  const shoulderCenterX = (leftShoulder.x + rightShoulder.x) / 2;

  // Check if face and shoulders are horizontally centered in frame
  const frameCenter = 0.5;
  const noseCenterDiff = Math.abs(nose.x - frameCenter);
  const shoulderCenterDiff = Math.abs(shoulderCenterX - frameCenter);

  const horizontallyCentered =
    noseCenterDiff < POSE_VALIDATION.CENTER_MAX_DIFF &&
    shoulderCenterDiff < POSE_VALIDATION.CENTER_MAX_DIFF;

  // Check vertical positioning - head should be in upper portion, not too high or low
  const verticallyPositioned =
    nose.y > POSE_VALIDATION.VERTICAL_POSITION_MIN &&
    nose.y < POSE_VALIDATION.VERTICAL_POSITION_MAX;

  return horizontallyCentered && verticallyPositioned;
};

// Face validation - requires key facial landmarks to be visible
const validateFaceVisible = (pose) => {
  if (!pose) return false;

  // MediaPipe Pose face landmarks
  const nose = pose[0]; // Nose tip - center of face
  const leftEye = pose[1]; // Left eye (inner)
  const rightEye = pose[4]; // Right eye (inner)
  const mouth = pose[9]; // Mouth (left)

  // Relaxed visibility requirements
  const noseVisible = nose && nose.visibility > VISIBLE;
  const leftEyeVisible = leftEye && leftEye.visibility > VISIBLE;
  const rightEyeVisible = rightEye && rightEye.visibility > VISIBLE;
  const bothEyesVisible = leftEyeVisible && rightEyeVisible;
  const mouthVisible = mouth && mouth.visibility > VISIBLE;

  // Face should be in reasonable position in frame
  const faceInFrame =
    nose &&
    nose.x > POSE_VALIDATION.FACE_X_MIN &&
    nose.x < POSE_VALIDATION.FACE_X_MAX && // More lenient horizontal
    nose.y > POSE_VALIDATION.FACE_Y_MIN &&
    nose.y < POSE_VALIDATION.FACE_Y_MAX; // More lenient vertical

  return noseVisible && bothEyesVisible && mouthVisible && faceInFrame;
};

// Runs MediaPipe pose detection to validate candidate positioning against the
// on-screen outline (used for video-proctored camera check step).
export const useProctoringValidation = ({
  isVideoProctoringEnabled,
  isMediaPipeReady,
  getDetectors,
  videoRef,
  overlayCanvasRef,
  outlineImage,
  cameraStatus,
  setCameraStatus,
  setErrorMessage,
  handleNextStep,
}) => {
  const [poseDetector, setPoseDetector] = useState(null);
  const [proctoringValidation, setProctoringValidation] = useState({
    faceVisible: false,
    bodyVisible: false,
    uprightPosture: false,
    centeredPosition: false,
  });
  const [validationCount, setValidationCount] = useState(0);
  const [isValidationActive, setIsValidationActive] = useState(false);
  const [validationStartTime, setValidationStartTime] = useState(null);
  const [showHints, setShowHints] = useState(false);

  const lastValidationRef = useRef(0);
  const validationTimeoutRef = useRef(null);
  const ownsDetectorRef = useRef(false);

  const resetValidationState = () => {
    setIsValidationActive(false);
    setValidationCount(0);
    setValidationStartTime(null);
    setShowHints(false);
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }
  };

  const disposePoseDetector = () => {
    if (poseDetector) {
      if (ownsDetectorRef.current && typeof poseDetector.close === "function") {
        try {
          poseDetector.close();
          console.log("UNMOUNT - Pose detector closed successfully");
        } catch (err) {
          console.error("UNMOUNT - Pose detector disposal error:", err);
        }
      }
      ownsDetectorRef.current = false;
      setPoseDetector(null);
    }
  };

  // Get pre-initialized pose detector from context
  const initializePoseDetector = async () => {
    if (!isVideoProctoringEnabled || poseDetector) return;

    if (isMediaPipeReady) {
      const detectors = getDetectors();
      if (detectors.poseDetector) {
        ownsDetectorRef.current = false;
        setPoseDetector(detectors.poseDetector);
        console.log("Using pre-loaded pose detector - no initialization lag!");
        return;
      }
    }

    // Fallback to manual initialization if context failed
    console.log(
      "MediaPipe context not ready, falling back to manual initialization"
    );
    try {
      const { PoseLandmarker, FilesetResolver } = await import(
        "@mediapipe/tasks-vision"
      );

      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
      );

      const detector = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
        outputSegmentationMasks: false,
      });

      ownsDetectorRef.current = true;
      setPoseDetector(detector);
      console.log("Pose detector initialized manually");
    } catch (error) {
      console.error("Error initializing pose detector:", error);
    }
  };

  // Optimized overlay drawing function
  const drawPositioningOverlay = (canvas, pose, validation, video) => {
    if (!canvas || !video || !outlineImage) return;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const canvasAspect = canvas.width / canvas.height;
    const imageAspect = outlineImage.width / outlineImage.height;

    let drawWidth, drawHeight, drawX, drawY;

    if (imageAspect > canvasAspect) {
      drawWidth = canvas.width * 1.2;
      drawHeight = drawWidth / imageAspect;
    } else {
      drawHeight = canvas.height * 0.9;
      drawWidth = drawHeight * imageAspect;
    }

    drawX = (canvas.width - drawWidth) / 2;

    const topOffset = canvas.height * 0.1;
    drawY = (canvas.height - drawHeight) / 2 - topOffset;

    drawY = Math.max(topOffset, drawY);

    drawDarkOutline(ctx, outlineImage, drawX, drawY, drawWidth, drawHeight);
  };

  const validateProctoringPosition = async () => {
    if (!videoRef.current || !poseDetector || !isValidationActive) {
      return;
    }

    const now = performance.now();
    // Throttle validation to 5 FPS for performance
    if (now - lastValidationRef.current < POSE_VALIDATION.POLL_INTERVAL_MS) return;
    lastValidationRef.current = now;

    const video = videoRef.current;
    if (!video.videoWidth || !video.videoHeight) {
      return;
    }

    try {
      // Get pose detection results
      const poseResults = await poseDetector.detectForVideo(video, now);
      const pose = poseResults.landmarks?.[0];

      const validation = {
        faceVisible: validateFaceVisible(pose),
        bodyVisible: validateBodyVisible(pose),
        uprightPosture: validateUprightPosture(pose),
        centeredPosition: validateCenteredPosition(pose),
      };

      setProctoringValidation(validation);

      if (!validationStartTime) {
        setValidationStartTime(Date.now());
      }

      // Count consecutive valid frames
      const allValid = Object.values(validation).every(Boolean);
      if (allValid) {
        setValidationCount((prev) => {
          const newCount = Math.min(
            prev + 1,
            POSE_VALIDATION.TARGET_VALID_FRAMES
          );

          if (
            newCount >= POSE_VALIDATION.TARGET_VALID_FRAMES &&
            cameraStatus !== "verified"
          ) {
            setCameraStatus("verified");
            setErrorMessage(null);
            setIsValidationActive(false);

            setTimeout(() => {
              handleNextStep();
            }, TIMING.VERIFIED_ADVANCE_DELAY_MS);
          }

          return newCount;
        });
      } else {
        setValidationCount((prev) =>
          Math.max(0, prev - POSE_VALIDATION.VALIDATION_DECAY_PER_BAD_FRAME)
        );
      }

      if (overlayCanvasRef.current) {
        drawPositioningOverlay(overlayCanvasRef.current, pose, validation, video);
      }
    } catch (error) {
      console.error("Validation error:", error);
    }
  };

  return {
    poseDetector,
    proctoringValidation,
    setProctoringValidation,
    validationCount,
    setValidationCount,
    isValidationActive,
    setIsValidationActive,
    validationStartTime,
    setValidationStartTime,
    showHints,
    setShowHints,
    initializePoseDetector,
    validateProctoringPosition,
    drawPositioningOverlay,
    resetValidationState,
    disposePoseDetector,
  };
};
